import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { getDraftSavedAtKey, recordDraftSaved } from '@states/drafts/draftSavedAt';

import type { TCalculateEquityCalculationsModel } from '@hooks/mutation/useMutationCalculateEquity';

export type TEquityCalculationAtom = Readonly<{
  quoteId?: string;
  calculations?: Maybe<TCalculateEquityCalculationsModel>;
  isFirstPaymentMade?: boolean;
}>;

export const INITIAL_EQUITY_CALCULATION_STATE: TEquityCalculationAtom = {
  quoteId: undefined,
  calculations: undefined,
  isFirstPaymentMade: undefined,
};

export const EQUITY_CALCULATION_ATOM_KEY_PREFIX = 'equityCalculationAtom_';

export const getEquityCalculationAtomKey = (quoteId: string): string =>
  `${EQUITY_CALCULATION_ATOM_KEY_PREFIX}${quoteId}`;

export const equityCalculationAtomFamily = atomFamily((quoteId: string) => {
  const storageAtom = atomWithStorage<TEquityCalculationAtom>(
    getEquityCalculationAtomKey(quoteId),
    { ...INITIAL_EQUITY_CALCULATION_STATE, quoteId },
  );

  return atom(
    (get) => get(storageAtom),
    (
      get,
      set,
      update:
        | Partial<TEquityCalculationAtom>
        | ((prev: TEquityCalculationAtom) => TEquityCalculationAtom),
    ) => {
      const current = get(storageAtom);
      const isQuoteMismatch = current.quoteId !== quoteId;

      const newValue = isQuoteMismatch
        ? { ...INITIAL_EQUITY_CALCULATION_STATE, quoteId }
        : typeof update === 'function'
          ? update(current)
          : { ...current, ...update };

      set(storageAtom, newValue);
      recordDraftSaved(getEquityCalculationAtomKey(quoteId));
    },
  );
});

export const removeEquityCalculationAtom = (quoteId: string): void => {
  equityCalculationAtomFamily.remove(quoteId);
  const storageKey = getEquityCalculationAtomKey(quoteId);
  if (typeof window !== 'undefined') {
    localStorage.removeItem(storageKey);
    localStorage.removeItem(getDraftSavedAtKey(storageKey));
  }
};
